import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Button from '@liquidweb-dev/undercurrent/Button';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import CopyButton from '@liquidweb-dev/undercurrent/CopyButton';
import getAppConfigs from 'modules/appConfig/reducer';
import useAccountDetails from 'modules/queries/account/useDetails';
import TrustPilotStarSVG from 'components/icons/TrustPilotLogoSvg';
import TrustPilotStars from 'components/icons/TrustPilotStars';

export interface ReferAFriendDialogProps extends Omit<DialogProps, 'onClose'> {
	onClose?: () => void;
}

const ReferAFriendDialog = ({
	open,
	onClose,
	...dialogProps
}: ReferAFriendDialogProps) => {
	const { data: accountUserDetailsData, isLoading: accountUserDetailsLoading } =
		useAccountDetails(
			{
				alsowith: ['referAFriend'],
			},
			{ enabled: Boolean(open) },
		);
	const appConfigs = getAppConfigs();
	const wwwHostname = `https://${appConfigs?.hosts?.www}`;
	const cartHostname = `https://${appConfigs?.hosts?.cart}`;

	const referAFriendCode = accountUserDetailsData?.referAFriend || '';
	const handleCopy = (event: React.MouseEvent, text: string) => {
		if (!text) {
			event.preventDefault();
			return;
		}
		navigator.clipboard.writeText(text);
	};

	return (
		<Dialog
			{...dialogProps}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			open={open}
		>
			<DialogTitle onClose={onClose}>
				<Typography fontWeight={500} variant="title-sm">
					Refer a friend,{' '}
					<Typography
						variant="title-sm"
						sx={(theme) => ({ color: theme.vars.palette.uc.success.text.main })}
					>
						get rewarded
					</Typography>
				</Typography>
			</DialogTitle>
			<DialogContent sx={{ paddingY: 300 }}>
				<Stack gap={200}>
					<Typography>
						Introduce your friends and they&apos;ll receive a special discount
						on their hosting plan. Once they&apos;ve been with us for a short
						time, you&apos;ll earn hosting credits as our thank-you.
					</Typography>
					{accountUserDetailsLoading && (
						<Skeleton
							variant="rounded"
							sx={{ height: '40px', width: '550px' }}
						/>
					)}
					{!accountUserDetailsLoading && (
						<Stack
							flexDirection="row"
							alignItems="center"
							justifyContent="space-between"
							gap={100}
						>
							<TextField
								disabled
								fullWidth
								sx={{
									'& input': {
										textOverflow: 'ellipsis',
									},
								}}
								value={`${cartHostname}/refer-a-friend/${referAFriendCode}`}
							/>
							<CopyButton
								iconButtonProps={{
									variant: 'barebones',
									sx: ({ size }) => ({
										height: size.xS,
										width: size.xS,
									}),
								}}
								tooltipProps={{ placement: 'right' }}
								onClick={(event) =>
									handleCopy(
										event,
										`${cartHostname}/refer-a-friend/${referAFriendCode}`,
									)
								}
							/>
						</Stack>
					)}
					<Stack flexDirection="row" justifyContent="space-between">
						<Stack
							flexDirection="row"
							gap={100}
							justifyContent="center"
							alignItems="center"
						>
							<TrustPilotStarSVG />
							<Stack
								flexDirection="row"
								alignItems="flex-end"
								gap={100}
								mt={50}
							>
								<TrustPilotStars />
								<Typography variant="label-md" fontWeight={500}>
									Excellent
								</Typography>
							</Stack>
						</Stack>
						<Stack>
							<Button
								variant="primary"
								size="large"
								onClick={() =>
									window.open(`${wwwHostname}/refer-a-friend/`, '_blank')
								}
							>
								Learn More
							</Button>
						</Stack>
					</Stack>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

export default ReferAFriendDialog;
